import { CommonModule } from '@angular/common';
import { Component, inject, type OnInit, signal, type WritableSignal } from '@angular/core';
import { FormBuilder, type FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime } from 'rxjs/operators';

import { AuthService } from '@auth/repository';
import { sessionUser$ } from '@auth/repository/auth.repository';
import type { IEntityLookupBean } from '@core/models/app.model';
import type { IUserSession } from '@core/models/user.model';
import { LOGIN_ACTION } from '@shared/constants/ume-constants';
import { CompanyTypePipe } from '@shared/pipes/company-type.pipe';
import { UmeFlagComponent } from '@widgets/ume-flag/ume-flag.component';

@UntilDestroy()
@Component({
  selector: 'ume-select-entity-dialog',
  templateUrl: './select-entity-dialog.component.html',
  styleUrls: ['./select-entity-dialog.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatTableModule,
    CompanyTypePipe,
    MatInput,
    UmeFlagComponent,
  ],
})
export class SelectEntityDialogComponent implements OnInit {
  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly authService: AuthService = inject(AuthService);
  public readonly dialogRef: MatDialogRef<SelectEntityDialogComponent> = inject(
    MatDialogRef<SelectEntityDialogComponent>
  );
  public data: { label: string; showClose: boolean } = inject(MAT_DIALOG_DATA);

  public showClose: WritableSignal<boolean> = signal<boolean>(false);
  public canNotLogin: WritableSignal<boolean> = signal<boolean>(false);
  public form: FormGroup = this._fb.group({
    search: null,
  });
  public linkedCompanies: IEntityLookupBean[] = [];
  public displayedColumns: string[] = ['new', 'country', 'name', 'type'];
  public dataSource!: MatTableDataSource<IEntityLookupBean>;

  ngOnInit(): void {
    if (this.data.showClose) {
      this.showClose.set(this.data.showClose);
    }

    sessionUser$.pipe(untilDestroyed(this)).subscribe((user: IUserSession) => {
      if (user.loginAction === LOGIN_ACTION.CONTACT_UME) {
        this.canNotLogin.set(true);
      } else {
        this.linkedCompanies = user.linkedCompanies;
        this.dataSource = new MatTableDataSource(this.linkedCompanies);
      }
    });

    this.form
      .get('search')
      ?.valueChanges.pipe(debounceTime(400), untilDestroyed(this))
      .subscribe((search: string) => {
        this.dataSource.filter = search.trim().toLowerCase();
      });
  }

  public logout(): void {
    this.authService.logout();
    this.dialogRef.close(null);
  }

  public selectedCompany(row: IEntityLookupBean): void {
    if (!row.canLogin) {
      return;
    }

    this._close(this.linkedCompanies.find((c) => c.guid === row.guid) || this.linkedCompanies[0]);
  }

  private _close(company: IEntityLookupBean): void {
    this.dialogRef.close(company);
  }
}
